<template>
  <Dropdown
    ref="containerRef"
    :shown
    :container
    :placement
    :disabled="isDropdownDisabled"
    auto-boundary-max-size
    strategy="fixed"
    v-bind="pick($attrs, ['class'])"
    :triggers="[]"
    :auto-hide="false"
  >
    <UIInputText
      v-model="inputValue"
      v-bind="omit($attrs, ['class'])"
      :disabled
      :readonly
      @ready="handleReady"
      @click:enter="handleClickEnter"
      @click:tab="handleClickTab"
      @click:escape="handleClickEscape"
      @blur="handleBlur"
      @focus="handleFocus"
      @paste="handlePaste"
    >
      <template v-if="$slots.leading" #leading>
        <slot name="leading" />
      </template>
      <template #trailing>
        <CalendarIcon
          aria-hidden="true"
          class="cursor-pointer"
          @click="handleClickCalendar"
        />
      </template>
    </UIInputText>
    <template #popper>
      <UIDatePicker
        ref="popperRef"
        v-model="modelValue"
        @update:model-value="handleSelectDate"
      />
    </template>
  </Dropdown>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watchEffect } from 'vue'
import { omit, pick } from 'lodash'
import { Dropdown, Placement } from 'floating-vue'
import { MaybeElement, onClickOutside } from '@vueuse/core'
import Inputmask from 'inputmask'

import { InputTextEmits } from './Text/utils/types'

import { useHotKeys } from '@/plugins/hotKeys'
import { useNotifications } from '@/plugins/notification'
import { convertDateToISO, parseDate, stringToLocalDateString } from '@/helpers/dates'

import { useUserSettingsStore } from '@/store/user/settings'

import UIDatePicker from '../DatePicker/DatePicker.vue'
import UIInputText from './Text/Text.vue'
import { CalendarIcon } from '@heroicons/vue/24/outline'

type Props = {
  disabled?: boolean
  readonly?: boolean

  container?: string
  placement?: Placement
}

type Emits = InputTextEmits

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const modelValue = defineModel<string | null>({
  default: null,
})

defineExpose({
  focus() {
    inputRef.value?.focus()
  },
  select() {
    inputRef.value?.select()
  },
})

defineOptions({
  inheritAttrs: false,
})

const { warn } = useNotifications()

const userSettingsStore = useUserSettingsStore()

const dateFormat = computed(() => userSettingsStore.getDateFormat)

const dateFormatMask = computed(() =>
  dateFormat.value?.toLowerCase().replace(/[ymd]/g, '9'),
)

const containerRef = useTemplateRef<MaybeElement>('containerRef')
const popperRef = useTemplateRef<typeof UIDatePicker>('popperRef')

const shown = ref(false)
const inputRef = ref<HTMLInputElement>()

const inputValue = computed({
  get() {
    return modelValue.value
      ? stringToLocalDateString(modelValue.value, {
        customFormat: dateFormat.value,
      }) || modelValue.value
      : undefined
  },
  set(value) {
    modelValue.value = value ? convertDateToISO(value) || value : null
  },
})

const isDropdownDisabled = computed(() => props.disabled || props.readonly)

const handleSelectDate = () => {
  shown.value = false
  inputRef.value?.focus()
}

const handleClickCalendar = () => {
  if (shown.value) {
    shown.value = false
    return
  }
  shown.value = true
  inputRef.value?.focus()
}

const handleReady = (el: HTMLInputElement) => {
  inputRef.value = el
  emit('ready', inputRef.value)
}

const handleClickEnter = (event: KeyboardEvent) => {
  emit('click:enter', event)
}

const handleClickTab = (event: KeyboardEvent) => {
  emit('click:tab', event)
}

const handleClickEscape = (event: KeyboardEvent) => {
  emit('click:escape', event)
}

const handleBlur = (event: HTMLInputElement) => {
  emit('blur', event)
}

const handleFocus = (event: HTMLInputElement) => {
  emit('focus', event)
}

const handlePaste = (event: ClipboardEvent) => {
  if (props.disabled || props.readonly) return
  event.preventDefault()

  const pastedText = event.clipboardData?.getData('text/plain')
  if (!pastedText) return

  const { isoDate, isAmbiguous } = parseDate(pastedText)

  if (!isoDate) return

  if (isAmbiguous) {
    warn({
      message: 'Unable to paste date',
      description: 'The pasted date is ambiguous and can have multiple formats.',
    })
    modelValue.value = null
    return
  }

  modelValue.value = isoDate
}

watchEffect(() => {
  if (!inputRef.value) return
  Inputmask.remove(inputRef.value)
  const maskSettings = {
    mask: dateFormatMask.value,
    placeholder: dateFormat.value?.toLowerCase(),
  }
  Inputmask(maskSettings).mask(inputRef.value)
})

onClickOutside(containerRef, event => {
  if (popperRef.value && event.composedPath().includes(popperRef.value.$el)) {
    return
  }
  shown.value = false
})

useHotKeys(
  'escape',
  () => {
    if (!shown.value) return
    shown.value = false
    return true
  },
  { priority: 110 },
)
</script>
